// GoogleLoginWrapper.tsx
import { stampSignupFlag } from '@common-components/ext-trial/ext-signup-flag';
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer';
import React from 'react';
import { useState } from "react";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { googleLoginThunk } from "@common-reducers/UserThunks";
import { isExtensionContext } from "@common-services/detect-context";
import GoogleLoginHandler from './google-login-handler';
import { AnalyticsService } from '@common-services/analytics-service';
import { useRouter } from "@common-reducers/hooks/use-router";
import { applySignupSignals, readAttributionCookie, readSignupSignals } from '@common/utils/attributionReader';
import { flowPath, isClearFlow } from '@common/utils/clear-flow';
import { backendApiEndpoints } from "@common-reducers/backend-api/backend-api";

const GoogleLoginWrapper: React.FC = () => {
  const dispatch = useAppDispatch();
  const [googleLoading, setGoogleLoading] = useState(false);
  const isExtension = isExtensionContext();
  const analyticsService: AnalyticsService = new AnalyticsService();
  const router = useRouter();

  const handleLoginSuccess = async (credential: string) => {
    setGoogleLoading(true);
    try {
     const res:any = await dispatch(googleLoginThunk({ accessToken: credential }));
      if (res.payload) {
        const {loginOrSignup} = res.payload
        analyticsService.event(loginOrSignup, {
          category: "access",
            action: loginOrSignup === "google_login_success" ? "google-login-success" : "google register new user",
            email: res.payload?.email
        });

        // NOTE: register_new_user / sign_up is now fired ONCE server-side for ALL
        // signups by the backend track-attribution hook (fires on the attribution
        // PATCH sent just below). The previous client-side emit here double-fired
        // with the server (the 1.26x multi-fire diagnosed in Project B) and was
        // removed. The google_register_new_user event above is kept as a
        // google-specific signal.

        if (loginOrSignup === 'google_register_new_user') {
          // Send attribution to backend for both web and extension signups.
          // readAttributionCookie() falls back to chrome.storage cache in extension context.
          // #1060: the fallback payload also carries the consent decision / Meta ids, so a visitor who
          // accepted marketing is not lost just because no attribution cookie existed.
          const attribution = readAttributionCookie() ?? applySignupSignals({
            first_touch: { utm_source: 'direct', utm_medium: 'none', timestamp: new Date().toISOString(), page_url: '', referrer: '' },
            conversion_ids: { gclids: [], fbclids: [], msclkids: [], ttclids: [] },
            last_updated: new Date().toISOString(),
          }, readSignupSignals());
          try {
            await dispatch(backendApiEndpoints.patchUser.initiate({
              id: res.payload?.user?._id,
              attribution,
            }));
          } catch (e) {
            console.warn('Failed to patch attribution:', e);
          }

          if (isExtension) {
            dispatch(accessFormPopupActions.markSignedUp());
            stampSignupFlag();
          } else {
            router.navigate(isClearFlow() ? flowPath('/calendar?welcome=true') : '/onboarding?from=signup');
          }
        }
      }
    } catch (error) {
      console.error("Error during Google login:", error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLoginError = () => {
    console.log('Login Failed');
  };

  return (
    <GoogleLoginHandler
      onLoginSuccess={handleLoginSuccess}
      onLoginError={handleLoginError}
      isExtension={isExtension}
      googleLoading={googleLoading}
    />
  );
};

export default GoogleLoginWrapper;
